import { AccountInfo } from '@azure/msal-browser';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import { FC, useEffect, useState } from 'react';
import { RouteProps } from 'react-router-dom';
import { intersection } from 'ramda';
import environment from '../../environments/environment';

// TODO - Make a dedicated component/page for this
const Message = ({ message }: { message: [string, string] }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <h4 data-testid={message[1]}>{message[0]}</h4>
  </div>
);

/**
  Handles route-based RBAC
*/
interface RouteGuardProps {
  roles?: string[];
  content: JSX.Element;
  loginPage: JSX.Element;
}

export const RouteGuard: FC<RouteGuardProps & RouteProps> = ({
  roles,
  content,
  loginPage,
}) => {
  const { tenantId, clientId } = environment.msalConfig.auth;
  const [isAuthorized, setAuthStatus] = useState(false);
  const [message, setMessage] = useState<[string, string]>(['', '']);
  const { instance } = useMsal();
  useEffect(() => {
    const onLoad = async () => {
      const currentAccount: AccountInfo | null = instance.getActiveAccount();
      const isLoggedIn = !!currentAccount;
      const isCorrectTenant = currentAccount?.tenantId === tenantId;

      // defer -> show login page if user is not logged in
      if (!isLoggedIn) {
        return setAuthStatus(true);
      }

      // fail -> show message if tenant ID doesn't match
      if (!isCorrectTenant) {
        return setMessage([
          'Your organization does not have access this content.',
          'invalid-tenant',
        ]);
      }

      // fail -> something else went wrong (incorrect audience)
      if (currentAccount.idTokenClaims?.aud !== clientId) {
        return setMessage([
          'The application you authorized with cannot access this page. Please contact site administrator.',
          'invalid-audience',
        ]);
      }

      // success -> no further action required if user is authenticated and no role-based auth is needed
      if (!roles || !roles.length) {
        return setAuthStatus(true);
      }

      // check user has required role
      const matched = intersection(
        roles,
        currentAccount.idTokenClaims?.roles || [],
      );

      // fail -> user not authorized
      if (!matched.length) {
        return setMessage([
          "You don't have the required role to view this page. Please contact site administrator.",
          'not-authorized',
        ]);
      }

      // success -> user has a required role
      setAuthStatus(true);
    };
    onLoad();
  }, [tenantId, clientId, isAuthorized, roles, instance]);

  return isAuthorized ? (
    <>
      <AuthenticatedTemplate>{content}</AuthenticatedTemplate>
      <UnauthenticatedTemplate>{loginPage}</UnauthenticatedTemplate>
    </>
  ) : (
    <Message message={message} />
  );
};
